import { useMemo } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@apac/ui";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import {
  CancellationPolicyEnum,
  type CfarContract,
  type HotelItinerary,
  type Reservation,
} from "@b2bportal/lodging-api";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import type { PlatformComponentProps } from "@overrides/types";
import { useCheckInPolicies } from "@tenant/hotels";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./HotelPolicies.module.scss";
import {
  formatCfarServiceExpiryDate,
  useCfarRefundAmount,
} from "@/utilities/hotels/cfar";

export interface HotelPoliciesProps extends PlatformComponentProps {
  reservation: Reservation;
  itinerary?: HotelItinerary;
  isActiveBooking?: boolean;
  onQueryCancellation?: () => void;
  cfarContract?: CfarContract;
  onCfarExercise?: () => void;
}

export const HotelPolicies = (props: HotelPoliciesProps) => {
  const {
    className,
    reservation,
    isActiveBooking,
    onQueryCancellation,
    cfarContract,
    onCfarExercise,
  } = props;
  const {
    context: { checkInPolicies },
    handlers,
  } = useCheckInPolicies({
    checkInPolicies: reservation.lodgingData.checkInInstructions.policies,
  });
  const [block, cn] = useModuleBEM(styles, "HotelPolicies");
  const { t, brand } = useI18nContext();

  const termsUrl =
    brand.urls.hotelCfarTerms ??
    brand.urls.cfarTerms ??
    brand.urls.termsAndConditions ??
    "";

  const hasCfar = cfarContract != null && onCfarExercise != null;

  const { cancellationPolicy } = reservation;
  const isRefundable =
    cancellationPolicy.CancellationPolicy === CancellationPolicyEnum.Refundable;
  const showSecondaryText =
    cancellationPolicy.CancellationPolicy ===
      CancellationPolicyEnum.NonRefundable || isRefundable;

  const cancellationContent = hasCfar ? (
    <CfarCancellationContent
      cn={cn}
      cfarContract={cfarContract}
      onCancelWithCfarClick={onCfarExercise}
      termsUrl={termsUrl}
      isActiveBooking={isActiveBooking}
    />
  ) : (
    <DefaultCancellationContent
      cn={cn}
      cancellationPolicy={cancellationPolicy}
      showSecondaryText={showSecondaryText}
    />
  );

  return (
    <div className={clsx(block, className)}>
      <div className={cn("cancellation")}>
        <h3 className={cn("title")}>{t("cancellationPolicyCopy")}</h3>
        {cancellationContent}
        {isActiveBooking && !hasCfar && onQueryCancellation != null && (
          <div className={cn("cancel-action")}>
            <button
              className={cn("cancel-button")}
              onClick={onQueryCancellation}
              type="button"
              aria-label={`${t("hotelTrips.cancelBooking")} - ${reservation.lodgingData?.name}`}
            >
              {t("hotelTrips.cancelBooking")}
            </button>
          </div>
        )}
      </div>

      {checkInPolicies != null && checkInPolicies.length > 0 && (
        <Accordion className={cn("accordion")}>
          {checkInPolicies.map(({ title, text }) => {
            if (text === "") return null;

            const content = handlers.cleanPolicy(text).map((item, index) => (
              <div key={index} className={cn("policy-item")}>
                {item}
              </div>
            ));

            return (
              <AccordionItem key={title} value={title}>
                <AccordionTrigger className={cn("accordion-trigger")}>
                  <span className={cn("accordion-title")}>{title}</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className={cn("policy-content")}>{content}</div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </div>
  );
};

interface CfarCancellationContentProps {
  cn: (element: string) => string;
  cfarContract?: CfarContract;
  onCancelWithCfarClick: () => void;
  termsUrl: string;
  isActiveBooking: boolean;
}

const CfarCancellationContent = ({
  cn,
  cfarContract,
  onCancelWithCfarClick,
  termsUrl,
  isActiveBooking,
}: CfarCancellationContentProps) => {
  const { t, language } = useI18nContext();
  const hasExpirationAndCoverage =
    cfarContract?.exerciseExpiration != null && cfarContract?.coverage != null;

  const refundAmount = useCfarRefundAmount(cfarContract);

  const secondaryText = useMemo(() => {
    if (!hasExpirationAndCoverage) {
      return null;
    }
    const values = {
      serviceExpiryDate: formatCfarServiceExpiryDate(
        cfarContract.exerciseExpiration,
        language
      ),
      refundAmount,
    };

    return cfarContract.coverageIncludesPremium
      ? t("hotelCard.cfarCancellationSecondaryTextFullyRefundable", values)
      : t("hotelCard.cfarCancellationSecondaryText", values);
  }, [hasExpirationAndCoverage, cfarContract, language, t, refundAmount]);

  const secondaryContent = useMemo(() => {
    if (!hasExpirationAndCoverage) {
      return null;
    }

    return (
      <>
        <span className={cn("cancellation-secondary")}>
          {secondaryText}{" "}
          {termsUrl !== "" && (
            <Trans
              i18nKey="hotelCard.cfarViewTerms"
              components={{
                linkText: (
                  // eslint-disable-next-line jsx-a11y/anchor-has-content -- visible link text is injected by <Trans> at runtime
                  <a
                    href={termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn("view-terms-link")}
                  />
                ),
              }}
            />
          )}
        </span>
        {isActiveBooking && (
          <span className={cn("action-button-container")}>
            <button
              className={cn("action-button")}
              onClick={onCancelWithCfarClick}
              type="button"
            >
              {t("selfServe.cancelForAnyReason")}
            </button>
          </span>
        )}
      </>
    );
  }, [
    hasExpirationAndCoverage,
    cn,
    secondaryText,
    termsUrl,
    t,
    isActiveBooking,
    onCancelWithCfarClick,
  ]);

  return (
    <div className={cn("cancellation-content")}>
      <div className={cn("cfar")}>
        <Icon
          iconName={IconNameEnum.cancelForAnyReason}
          className={cn("cfar-icon")}
        />
        <span className={cn("cfar-label")}>
          {t("selfServe.cancelForAnyReason")}
        </span>
      </div>
      {secondaryContent}
    </div>
  );
};

interface DefaultCancellationContentProps {
  cn: (element: string) => string;
  cancellationPolicy: Reservation["cancellationPolicy"];
  showSecondaryText: boolean;
}

const DefaultCancellationContent = ({
  cn,
  cancellationPolicy,
  showSecondaryText,
}: DefaultCancellationContentProps) => (
  <div className={cn("cancellation-content")}>
    {"primaryText" in cancellationPolicy && (
      <span className={cn("cancellation-primary")}>
        {cancellationPolicy.primaryText}
      </span>
    )}
    {showSecondaryText &&
      "secondaryText" in cancellationPolicy &&
      cancellationPolicy.secondaryText && (
        <span className={cn("cancellation-secondary")}>
          {cancellationPolicy.secondaryText}
        </span>
      )}
  </div>
);
